import React, {
  createContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  buscarTaxaSelic,
  obterBasesCalculo,
  obterFolhas,
  obterImpostos,
  obterVerbas,
  obterVerbasAjustadas,
  resumoGeral,
} from "../../services/api-esocial.service";
import { buscarComboPorCodigo } from "../../services/auth.service";
import {
  buscarVersaoResumoGeral,
  editarVersaoPrevia,
  editarVersaoProjeto,
} from "../../services/api-inss.service";
import {
  filterFolhaItemsByCnpjCoherence,
  normalizeCnpjDigits,
} from "../../utils/previaInssFolhaCoherence";

const PreviaInssContext = createContext();

const getSchemaByCnpj = (cnpjRaw) => {
  const cnpjDigits = String(cnpjRaw || "").replace(/\D/g, "");
  return cnpjDigits ? `z_${cnpjDigits}` : null;
};

const PreviaInssProvider = ({ children }) => {
  const [dados, setDados] = useState({
    verbasEsocial: [],
    verbasComValores: [],
    relatorio20s: [],
    verbasAjustadas: [],
    impostos: [],
    baseCalculo: [],
    resumo: [],
    resumoAtual: [],
    contasResumo: [],
    idEmpresa: null,
    loading: false,
    loadingVersao: false, // Estado específico para busca de versão (não bloqueia tela)
    idVersao: null,
    tipoVersao: null, // 'previa' | 'projeto'
    isEditable: true, // true quando a versão atual é a mais recente; definido ao abrir versão
  });

  const [taxaSelic, setTaxaSelic] = useState([]);

  const handleAtualizarImpostos = async (id) => {
    const impostosResponse = await obterImpostos(id);
    setDados((prev) => ({
      ...prev,
      impostos: impostosResponse.impostos || [],
    }));
    return impostosResponse.impostos || [];
  };

  // ======= formatter & organiza =======
  const formatter = useCallback((sourceData, format) => {
    switch (format) {
      case "monetary-from-report":
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(parseFloat(String(sourceData).trim()));
      case "monetary":
        return sourceData
          ? new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
            }).format(parseFloat(sourceData))
          : "R$ 0,00";
      case "date":
        return String(sourceData).split("-").reverse().join("/");
      case "date-from-report":
        return new Date(sourceData).toLocaleDateString("pt-BR");
      case "rubricCode":
        return String(sourceData).replace(/([A-Z]+)(0+)(\d+)/, "$1...$3");
      case "cnpj":
        return String(sourceData).replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
          "$1.$2.$3/$4-$5"
        );
      default:
        return sourceData;
    }
  }, []);

  const handleOrganizaData = useCallback(
    (info) => {
      return (info || []).map((item) => ({
        ...item,
        codigoRubricaCompleto: item.codigoRubrica, // original
        totalCprb: formatter(item.totalCprb, "monetary"),
        cargaSefipCprb: `${item.cnpj}|${item.periodoApuracao.split("-")[0]}|${
          item.periodoApuracao.split("-")[1]
        }|${item.totalCprbCorrigido ? item.totalCprbCorrigido : "0"}`,
        totalCprbCalculado: formatter(item.totalCprbCalculado, "monetary"),
        totalCprbCorrigido: formatter(item.totalCprbCorrigido, "monetary"),
        inss: formatter(item.inss, "monetary"),
        cnpj: formatter(item.cnpj, "cnpj"),
        inssCorrigido: formatter(item.inssCorrigido, "monetary"),
        periodoApuracao: formatter(item.periodoApuracao, "date"),
        codigoRubrica: formatter(item.codigoRubrica, "rubricCode"),
        descricaoRubrica: item.descricaoRubrica,
        incidenciaRubrica: item.incidenciaRubrica,
        valorRubrica: formatter(item.valorRubrica, "monetary"),
        totalGeral: formatter(item.totalGeral, "monetary"),
        totalGeralCorrigido: formatter(item.totalGeralCorrigido, "monetary"),
        totalGeralCalculado: formatter(item.totalGeralCalculado, "monetary"),
      }));
    },
    [formatter]
  );

  // ======= trava & hash para dedupe de persistência =======
  const resumoPersistRef = React.useRef({
    inFlight: false,
    lastHash: null,
    lastVersaoId: null,
  });

  const hashResumo = (lista = []) => {
    const base = lista.map((r) => [
      String(r.codigoRubricaCompleto ?? r.codigoRubrica ?? ""),
      String(r.periodoApuracao ?? ""),
      String(r.totalGeral ?? ""),
      String(r.totalGeralCorrigido ?? ""),
      String(r.totalGeralCalculado ?? ""),
    ]);
    return JSON.stringify(base);
  };

  // ======= buscar resumo GERAL da VERSÃO + persistir uma vez =======
  // Ref para acessar dados mais recentes sem causar recriação do callback
  const dadosRef = React.useRef(dados);
  React.useEffect(() => {
    dadosRef.current = dados;
  }, [dados]);

  const handleBuscarResumoGeralVersao = useCallback(async () => {
    // Proteção contra chamadas simultâneas
    if (resumoPersistRef.current.inFlight) {
      return [];
    }
    
    try {
      // Usar dados mais recentes do ref
      const snapshot = { ...dadosRef.current };

      // helpers
      const getCodigo = (obj) =>
        obj?.codRubrica ??
        obj?.codigoRubrica ??
        obj?.codrubr ??
        obj?.codigo_rubrica;

      const getPeriodoYYYYMM = (s) => (s ? String(s).slice(0, 7) : null);

      const getBoolComposicaoProjeto = (obj) =>
        obj?.composicaoProjeto === true ||
        obj?.composicao_projeto === true ||
        obj?.composicao === true;

      const getInicioVal = (obj) =>
        getPeriodoYYYYMM(
          obj?.inicioValidade ??
            obj?.inicio_validade ??
            obj?.inicio_valid ??
            obj?.inicio
        );

      const getFimVal = (obj) =>
        getPeriodoYYYYMM(
          obj?.fimValidade ??
            obj?.final_validade ??
            obj?.fim_validade ??
            obj?.fim
        ) || "9999-12";

      // 1) ranges por código com base nas verbasAjustadas que compõem
      const rangesPorCodigo = new Map();
      (snapshot.verbasAjustadas || [])
        .filter((aj) => getBoolComposicaoProjeto(aj))
        .forEach((aj) => {
          const codigo = getCodigo(aj);
          const inicio = getInicioVal(aj);
          const fim = getFimVal(aj);
          if (!codigo || !inicio) return;
          if (!rangesPorCodigo.has(codigo)) rangesPorCodigo.set(codigo, []);
          rangesPorCodigo.get(codigo).push([inicio, fim]);
        });

      // 2) folha filtrada por código+intervalo (+ coerência CNPJ × código da rubrica)
      const cnpjSnap = normalizeCnpjDigits(
        snapshot?.cnpj || sessionStorage.getItem("cnpjSelecionado")
      );
      const verbasCoherent = filterFolhaItemsByCnpjCoherence(
        snapshot.verbasComValores || [],
        cnpjSnap
      );
      const folhaFiltrada = verbasCoherent.filter((item) => {
        const codigo = getCodigo(item);
        if (!codigo) return false;

        const periodo = getPeriodoYYYYMM(
          item?.periodoApurado ?? item?.periodo_apurado ?? item?.periodo
        );
        if (!periodo) return false;

        const ranges = rangesPorCodigo.get(codigo);
        if (!ranges || ranges.length === 0) return false;

        return ranges.some(([ini, fim]) => {
          const okIni = !ini || periodo >= ini;
          const okFim = !fim || periodo <= fim;
          return okIni && okFim;
        });
      });

      // 3) selic
      const taxaSelicResp = await buscarTaxaSelic();
      const selicAcumulada = (taxaSelicResp?.taxaSelic || [])
        .map((t) => {
          const referencia = getPeriodoYYYYMM(
            t?.referencia ?? t?.competencia ?? t?.periodo ?? t?.data
          );
          const taxaNum = Number(
            t?.taxa ??
              t?.valor ??
              t?.taxaAcumulada ??
              t?.acumulada ??
              t?.tx ??
              t?.percentual ??
              t?.taxa_selic ??
              t?.selic
          );
          return referencia && !Number.isNaN(taxaNum)
            ? { referencia, taxa: taxaNum }
            : null;
        })
        .filter(Boolean);

      const payload = {
        impostos: (snapshot.impostos || []).map((item) => ({
          referencia: item.referencia,
          terceiros: Number(item.terceiros),
          rat: Number(item.rat),
          inss: Number(item.inss),
        })),
        selicAcumulada,
        folha: folhaFiltrada.map((item) => ({
          periodoApurado: item.periodoApurado,
          idTabelaRubrica: item.idTabelaRubrica,
          codRubrica: getCodigo(item),
          valorRubrica: item.valorRubrica,
          tipoRubrica: item.tipoRubrica,
          descricaoRubrica: item.descricaoRubrica,
          incidencia: item.incidencia,
          composicaoProjeto: true,
          verbaFerias: item.verbaFerias,
        })),
      };

      const schema =
        getSchemaByCnpj(snapshot?.cnpj) ||
        getSchemaByCnpj(sessionStorage.getItem("cnpjSelecionado"));
      if (!schema) {
        throw new Error("Não foi possível identificar o schema da empresa selecionada.");
      }
      const resposta = await buscarVersaoResumoGeral(schema, payload);

      const bruto = resposta?.resumos ?? resposta?.relatorioGeral ?? [];
      const resumoFormatado = handleOrganizaData(bruto) || [];

      // reflete na UI - apenas se realmente mudou para evitar loops
      setDados((prev) => {
        const currentHash = hashResumo(prev.resumo || []);
        const newHash = hashResumo(resumoFormatado);
        
        // Só atualiza se realmente mudou
        if (currentHash === newHash) {
          return prev;
        }
        
        return {
          ...prev,
          resumo: resumoFormatado,
          resumoAtual: resumoFormatado,
        };
      });

      // dedupe de persistência
      const nextHash = hashResumo(resumoFormatado);
      const versaoAtual = snapshot?.idVersao ?? null;

      if (
        resumoPersistRef.current.lastHash === nextHash &&
        resumoPersistRef.current.lastVersaoId === versaoAtual
      ) {
        return resumoFormatado;
      }

      if (resumoPersistRef.current.inFlight) {
        return resumoFormatado;
      }

      // Só persiste quando há id de versão válido (>= 1). Evita PUT /previas/versao/null
      const idValido = (() => {
        const raw = snapshot?.idVersao;
        const n = typeof raw === "number" ? raw : parseInt(raw, 10);
        return typeof n === "number" && !Number.isNaN(n) && n >= 1;
      })();

      if (idValido) {
        resumoPersistRef.current.inFlight = true;
        await handleAtualizarVersao(
          {
            ...snapshot,
            verbasComValores: verbasCoherent,
            resumo: resumoFormatado,
          },
          snapshot.tipoVersao,
          snapshot.idVersao
        );
        resumoPersistRef.current.lastHash = nextHash;
        resumoPersistRef.current.lastVersaoId = versaoAtual;
        resumoPersistRef.current.inFlight = false;
      }

      return resumoFormatado;
    } catch (error) {
      console.error("Erro ao buscar/persistir resumo geral da versão:", error);
      resumoPersistRef.current.inFlight = false;
      return [];
    }
  }, [handleOrganizaData]);

  // ======= atualizar resumo (legado) =======
  const handleAtualizarResumo = async (id, idPreviaVersao = 0) => {
    try {
      if (idPreviaVersao > 0) {
        const snapshot = dados;
        const cnpjDigits = normalizeCnpjDigits(
          snapshot?.cnpj || sessionStorage.getItem("cnpjSelecionado")
        );
        const verbasOk = filterFolhaItemsByCnpjCoherence(
          snapshot.verbasComValores || [],
          cnpjDigits
        );
        const payload = {
          impostos: (snapshot.impostos || []).map((item) => ({
            referencia: item.referencia,
            terceiros: Number(item.terceiros),
            rat: Number(item.rat),
            inss: Number(item.inss),
          })),
          selicAcumulada: (snapshot.baseCalculo || []).map((item) => ({
            referencia: item.referencia,
            taxa: Number(item.taxaSelic),
          })),
          folha: verbasOk
            .filter((item) => item.composicaoProjeto)
            .map((item) => ({
              periodoApurado: item.periodoApurado,
              idTabelaRubrica: item.idTabelaRubrica,
              codRubrica: item.codRubrica,
              valorRubrica: item.valorRubrica,
              tipoRubrica: item.tipoRubrica,
              descricaoRubrica: item.descricaoRubrica,
              incidencia: item.incidencia,
              composicaoProjeto: item.composicaoProjeto,
              verbaFerias: item.verbaFerias,
            })),
        };

        const schema =
          getSchemaByCnpj(snapshot?.cnpj) ||
          getSchemaByCnpj(sessionStorage.getItem("cnpjSelecionado"));
        if (!schema) {
          throw new Error("Não foi possível identificar o schema da empresa selecionada.");
        }
        const resumoVersaoResponse = await buscarVersaoResumoGeral(
          schema,
          payload
        );

        const resumoFormatado =
          handleOrganizaData(
            resumoVersaoResponse?.resumos ??
              resumoVersaoResponse?.relatorioGeral ??
              []
          ) || [];
        setDados((prev) => ({ ...prev, resumo: resumoFormatado }));
        return resumoFormatado;
      }

      const resumoResponse = await resumoGeral({ cnpjs: [`${id}`] });
      const resumoFormatado = handleOrganizaData(resumoResponse.resumos) || [];
      setDados((prev) => ({ ...prev, resumo: resumoFormatado }));
      return resumoFormatado || [];
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const handleAtualizarVersao = useCallback(async (novaVersao, tipoVersaoOverride = null, idVersaoOverride = null) => {
    try {
      const tipoVersao = tipoVersaoOverride ?? dados.tipoVersao;
      const rawId = idVersaoOverride ?? dados.idVersao;
      const idVersao = typeof rawId === "number" ? rawId : parseInt(rawId, 10);

      // Não chama a API com id inválido (null, undefined, NaN, 0 ou negativo)
      if (idVersao == null || Number.isNaN(idVersao) || idVersao < 1) {
        return;
      }

      if (tipoVersao === "projeto") {
        await editarVersaoProjeto({ versao: novaVersao }, idVersao);
      } else {
        await editarVersaoPrevia({ versao: novaVersao }, idVersao);
      }
    } catch (error) {
      console.error("Erro ao atualizar versão:", error);
    }
  }, [dados.tipoVersao, dados.idVersao]);

  const buscaDadosPrevia = async (id) => {
    setDados((prev) => {
      const trocaEmpresa =
        prev.idEmpresa != null && String(prev.idEmpresa) !== String(id);
      return {
        ...prev,
        loading: true,
        idEmpresa: id,
        ...(trocaEmpresa
          ? {
              verbasEsocial: [],
              verbasComValores: [],
              relatorio20s: [],
              verbasAjustadas: [],
              impostos: [],
              baseCalculo: [],
              resumo: [],
              resumoAtual: [],
              contasResumo: [],
              cnpj: null,
              idVersao: null,
              tipoVersao: null,
            }
          : {}),
      };
    });
    try {
      const [
        esocialResponse,
        valoresResponse,
        relatorio20sResponse,
        verbasAjustadasResponse,
        impostosResponse,
        baseCalculoResponse,
        resumoGeralResponse,
        contasResumoResponse,
        taxaSelicResponse,
      ] = await Promise.all([
        obterVerbas(id),
        obterFolhas(id),
        obterBasesCalculo(id),
        obterVerbasAjustadas(id),
        obterImpostos(id),
        obterBasesCalculo(id),
        resumoGeral({ cnpjs: [`${id}`] }),
        buscarComboPorCodigo(`ESOCIAL_CONTAS_COMPOSICAO_${id}`),
        buscarTaxaSelic(),
      ]);

      const resumoFormatado = handleOrganizaData(resumoGeralResponse?.resumos ?? resumoGeralResponse?.data?.resumos) || [];
      const contasResumo = (contasResumoResponse?.detalhes?.itensCombo ?? contasResumoResponse?.data?.detalhes?.itensCombo ?? []).map((item) => {
        const [idc, label] = item.split("/|/");
        return { id: idc, label };
      });

      setTaxaSelic(taxaSelicResponse?.taxaSelic ?? taxaSelicResponse?.data?.taxaSelic ?? []);

      // Um único setDados com todos os campos + loading: false para evitar que outro setState sobrescreva
      const verbasEsocial = esocialResponse?.verbas ?? esocialResponse?.data?.verbas ?? [];
      const cnpjDigits = normalizeCnpjDigits(
        sessionStorage.getItem("cnpjSelecionado")
      );
      const verbasComValoresBruto =
        valoresResponse?.folha ?? valoresResponse?.data?.folha ?? [];
      const verbasComValores = filterFolhaItemsByCnpjCoherence(
        Array.isArray(verbasComValoresBruto) ? verbasComValoresBruto : [],
        cnpjDigits
      );
      const relatorio20s = relatorio20sResponse?.basesCalculo ?? relatorio20sResponse?.data?.basesCalculo ?? [];
      const verbasAjustadas = verbasAjustadasResponse?.verbas ?? verbasAjustadasResponse?.data?.verbas ?? [];
      const impostos = impostosResponse?.impostos ?? impostosResponse?.data?.impostos ?? [];
      const baseCalculo = baseCalculoResponse?.basesCalculo ?? baseCalculoResponse?.data?.basesCalculo ?? [];

      setDados((prev) => ({
        ...prev,
        cnpj: String(sessionStorage.getItem("cnpjSelecionado") || "").replace(/\D/g, "") || prev?.cnpj || null,
        verbasEsocial: Array.isArray(verbasEsocial) ? verbasEsocial : [],
        verbasComValores,
        relatorio20s: Array.isArray(relatorio20s) ? relatorio20s : [],
        verbasAjustadas: Array.isArray(verbasAjustadas) ? verbasAjustadas : [],
        impostos: Array.isArray(impostos) ? impostos : [],
        baseCalculo: Array.isArray(baseCalculo) ? baseCalculo : [],
        resumo: resumoFormatado,
        resumoAtual: resumoFormatado,
        contasResumo,
        loading: false,
      }));
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setDados((prev) => ({ ...prev, loading: false }));
    }
  };

  const contextValue = useMemo(
    () => ({
      buscaDadosPrevia,
      handleAtualizarImpostos,
      handleAtualizarResumo,
      handleAtualizarVersao,
      handleBuscarResumoGeralVersao,
      loading: dados.loading,
      loadingVersao: dados.loadingVersao,
      setLoading: (loading) => setDados((prev) => ({ ...prev, loading })),
      setLoadingVersao: (loadingVersao) => setDados((prev) => ({ ...prev, loadingVersao })),
      dados,
      setDados,
      taxaSelic,
    }),
    [dados, handleAtualizarResumo, handleAtualizarVersao, handleBuscarResumoGeralVersao, handleOrganizaData, taxaSelic]
  );

  return (
    <PreviaInssContext.Provider value={contextValue}>
      {children}
    </PreviaInssContext.Provider>
  );
};

export { PreviaInssContext, PreviaInssProvider };
